import { memo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRightIcon } from '@radix-ui/react-icons'

import { PaytrieAPI } from '@paytrie/client'
import { formatMoney } from '@paytrie/common/utils'
import { StatusBadge } from '@/components/StatusBadge'
import { formatCurrency, formatDate } from './dashboardDisplayUtils'
import { getStatusText, getStatusTone } from './transactionStatus'

type TransactionCardProps = {
  index: number
  transactionId: string
  status: PaytrieAPI.TransactionStatus
  date: string
  leftSideLabel: string
  leftSideValue: number
  rightSideLabel: string
  rightSideValue: number
}

export const TransactionCard = memo(function TransactionCard({
  index,
  transactionId,
  status,
  date,
  leftSideLabel,
  leftSideValue,
  rightSideLabel,
  rightSideValue,
}: TransactionCardProps) {
  const { t } = useTranslation()

  return (
    <Link
      to={`/Status?id=${transactionId}`}
      title={t('transactionRows.viewDetailsTooltip')}
      className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-cloudLighter p-4 dark:border-gray-700 dark:bg-darkCharcoal"
      data-testid={`card-transaction-${index}`}
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className="text-xs text-gray-500 dark:text-gray-400"
          data-testid={`card-transaction-${index}-date`}
        >
          {formatDate(date)}
        </span>
        <StatusBadge
          tone={getStatusTone(status)}
          testId={`card-transaction-${index}-status`}
        >
          {getStatusText(status)}
        </StatusBadge>
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[15px] font-semibold text-black dark:text-white">
        <span className="whitespace-nowrap">
          {formatMoney(leftSideValue)} {formatCurrency(leftSideLabel)}
        </span>
        <ArrowRightIcon className="h-4 w-4 shrink-0 text-gray-400" />
        <span className="whitespace-nowrap">
          {formatMoney(rightSideValue)} {formatCurrency(rightSideLabel)}
        </span>
      </div>
      <span
        className="break-all font-mono text-xs text-gray-400 dark:text-gray-500"
        data-testid={`card-transaction-${index}-id`}
      >
        {transactionId}
      </span>
    </Link>
  )
})
